<template>
  <sweet-modal
    v-if="isMounted && (!lazy || hasBeenOpened)"
    ref="modal"
    :class="{
      'dialog-modal-fi': true,
      'dialog-modal-fi--large-padding': largePadding,
      [`dialog-modal-fi--theme-${theme}`]: theme,
      'dialog-modal-fi--transparent-bg': transparentBackground,
      'dialog-modal-fi--auto-width': autoWidth,
      'dialog-modal-fi--wide': wide,
      'dialog-modal-fi--full-screen': fullScreen,
      'dialog-modal-fi--deligate-padding': deligatePadding,
      'dialog-modal-fi--narrow-1': narrow1,
      'dialog-modal-fi--center-content': centerContent,
      'dialog-modal-fi--is-mobile-fullscreen': mobileFullscreen,
    }"
    overlay-theme="dark"
    :enable-mobile-fullscreen="mobileFullscreen && theme !== 'bottom-layer-xs'"
    :blocking="preventCloseOnClickOutside"
    :pulse-on-block="pulseOnBlock"
    :hide-close-button="hideCloseButton"
    @open="setBodyHeight"
    @close="beforeClose"
  >
    <template
      v-if="$slots['box-action']"
      #box-action
    >
      <slot name="box-action" />
    </template>
    <template v-if="$slots.header">
      <div
        ref="modalHeader"
        class="dialog-modal-fi__header"
      >
        <slot name="header" />
      </div>
    </template>

    <div
      v-if="$slots.body"
      ref="modalBody"
      class="dialog-modal-fi__body"
    >
      <slot name="body" />
    </div>

    <template v-if="$slots.footer">
      <div
        ref="modalFooter"
        class="dialog-modal-fi__footer"
      >
        <slot name="footer" />
      </div>
    </template>
  </sweet-modal>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';

import { useBreakpoints } from '@/composables/breakpoint';

import SweetModal from './sweet-modal/SweetModal.vue';

defineOptions({
  name: 'DialogModalFi',
});

interface Props {
  onlyBodyScrollable?: boolean;
  mobileFullscreen?: boolean;
  largePadding?: boolean;
  preventCloseOnClickOutside?: boolean;
  noMaxHeight?: boolean;
  hideCloseButton?: boolean;
  pulseOnBlock?: boolean;
  theme?: string;
  transparentBackground?: boolean;
  autoWidth?: boolean;
  lazy?: boolean;
  wide?: boolean;
  fullScreen?: boolean;
  deligatePadding?: boolean;
  narrow1?: boolean;
  centerContent?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  onlyBodyScrollable: false,
  mobileFullscreen: true,
  largePadding: false,
  preventCloseOnClickOutside: false,
  noMaxHeight: false,
  hideCloseButton: false,
  pulseOnBlock: false,
  theme: '',
  transparentBackground: false,
  autoWidth: false,
  lazy: false,
  wide: false,
  fullScreen: false,
  deligatePadding: false,
  narrow1: false,
  centerContent: false,
});

const emit = defineEmits<{
  'after-open-transition': [];
  'after-close-transition': [];
  close: [];
  default: [];
  fullscreen: [];
  'body-resize': [height: string];
}>();

const { windowDimensions, isSM, isMD, isLG } = useBreakpoints();

const modal = useTemplateRef('modal');
const modalHeader = useTemplateRef('modalHeader');
const modalBody = useTemplateRef('modalBody');
const modalFooter = useTemplateRef('modalFooter');

const isFullscreen = ref(false);
const isDefault = ref(true);
const hasBeenOpened = ref(false);
const resizeListenerEnabled = ref(false);
const isMounted = ref(false);

const isBottomLayerTheme = computed(() => {
  return props.theme === 'bottom-layer' || props.theme === 'bottom-layer-xs';
});

const useSmallLayer = () => {
  return window.innerWidth < 600 && props.theme !== 'bottom-layer-xs';
};

const isOpen = () => {
  return modal.value?.is_open;
};

const open = async (): Promise<boolean | void> => {
  if (IS_SSR) {
    return false;
  }

  if (props.onlyBodyScrollable && !resizeListenerEnabled.value) {
    resizeListenerEnabled.value = true;
  }

  if (props.lazy && !hasBeenOpened.value) {
    hasBeenOpened.value = true;
    await nextTick();
  }

  if (!modal.value) {
    // most likely bubbling event
    return;
  }

  modal.value.open();
  await nextTick();

  // emit after open modal transition
  setTimeout(() => {
    emit('after-open-transition');
  }, 300);

  if (isBottomLayerTheme.value && !useSmallLayer()) {
    return false;
  }

  return true;
};

const beforeClose = () => {
  emit('close');

  // emit after close modal transition
  setTimeout(() => {
    emit('after-close-transition');
  }, 300);
};

const close = () => {
  if (!hasBeenOpened.value && props.lazy) {
    return;
  }
  modal.value?.close();
};

const setBodyHeight = async () => {
  if (IS_SSR) {
    return;
  }

  if (modalBody.value && useSmallLayer()) {
    if (!isFullscreen.value) {
      emit('fullscreen');
      isFullscreen.value = true;
      isDefault.value = false;

      modalBody.value.style.maxHeight = '';
      await nextTick();
    }
  } else if (!isDefault.value) {
    emit('default');
    isDefault.value = true;
    isFullscreen.value = false;
  }

  if (props.onlyBodyScrollable) {
    await nextTick();
    let modalPadding = isMD.value ? 48 + 30 : 48 + 18;
    if (props.largePadding && isLG.value) {
      modalPadding = 144;
    }

    const modalHeight = props.mobileFullscreen && !isSM.value ? '100vh' : '90vh';
    const headerHeight = modalHeader.value ? modalHeader.value.offsetHeight + 30 : 0;
    const footerHeight = modalFooter.value ? modalFooter.value.offsetHeight + 30 : 0;
    const bodyHeight = `calc(${modalHeight} - ${headerHeight + footerHeight + modalPadding}px)`;

    if (modalBody.value && !props.noMaxHeight) {
      modalBody.value.style.maxHeight = bodyHeight;

      await nextTick();
      setTimeout(() => {
        const modalElement = document.querySelector('.sweet-modal.is-visible');
        if (!modalElement) {
          return;
        }
        const modalBoundingHeight = modalElement.getBoundingClientRect().height;
        const modalOffsetTopBottom = useSmallLayer() ? window.innerHeight - modalBoundingHeight : 0;
        const sweetContent = document.querySelector('.sweet-modal.is-visible .sweet-content');
        const sweetContentHeight = sweetContent?.getBoundingClientRect().height || 0;
        const bodyHeightWithOffset = `calc(${modalHeight} - ${
          modalOffsetTopBottom + headerHeight + footerHeight + modalPadding
        }px)`;

        if (useSmallLayer() && modalBoundingHeight < sweetContentHeight) {
          if (modalBody.value) {
            modalBody.value.style.maxHeight = bodyHeightWithOffset;
          }
        }

        emit(
          'body-resize',
          `calc(${modalHeight} - ${
            modalBoundingHeight < sweetContentHeight
              ? modalOffsetTopBottom + modalPadding
              : modalPadding
          }px)`,
        );
      }, 200);
    }
  }
};

const scrollToTop = () => {
  if (modalBody.value) {
    modalBody.value.scrollTop = 0;
  }
};

watch(
  () => windowDimensions.value,
  () => {
    if (!resizeListenerEnabled.value) {
      return;
    }

    setBodyHeight();
  },
  { deep: true },
);

onMounted(() => {
  isMounted.value = true;
});

defineExpose({
  open,
  close,
  isOpen,
  scrollToTop,
  setBodyHeight,
});
</script>

<style src="./dialog-modal-fi" />
